import React, { useState, useEffect, useRef } from 'react';
import { ArrowUp } from 'lucide-react';

/**
 * A floating "Back to Top" button that appears after scrolling down.
 * Automatically finds the nearest scrollable ancestor (e.g. dashboard <main>).
 * Auto-hides after 3 seconds of no scrolling.
 */
const ScrollToTop: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const locatorRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    // Walk up the DOM to find the closest scrollable ancestor
    let el: HTMLElement | null = locatorRef.current;
    while (el) {
      const style = getComputedStyle(el);
      if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
        scrollContainerRef.current = el;
        break;
      }
      el = el.parentElement;
    }
    const target = scrollContainerRef.current || window;
    const handleScroll = () => {
      const scrollY = scrollContainerRef.current ? scrollContainerRef.current.scrollTop : window.scrollY;
      const scrolled = scrollY > 600;
      setVisible(scrolled);
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
      if (scrolled) {
        scrollTimerRef.current = setTimeout(() => setVisible(false), 3000);
      }
    };
    target.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      target.removeEventListener('scroll', handleScroll);
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    };
  }, []);

  return (
    <>
      {/* Invisible anchor used to locate the scrollable ancestor */}
      <span ref={locatorRef} className="hidden" />
      <button
        onClick={() => {
          const target = scrollContainerRef.current || window;
          target.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 min-h-[44px] ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        <ArrowUp className="w-4 h-4" />
        <span className="text-sm font-medium">Back to Top</span>
      </button>
    </>
  );
};

export default ScrollToTop;
